"use client";

// React and hooks
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Icons
import {
  X,
  User,
  Building2,
  Mail,
  Phone,
  CheckCircle2,
  Send,
  AlertCircle,
  Loader2
} from "lucide-react";

// Styles
import { getBaseUrl } from "../config/apiConfig";

function RegistrationForm({ onClose }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    consent: false,
    areas: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [serverError, setServerError] = useState("");
  const { t, i18n } = useTranslation();

  const bodyAreas = [
    { value: "Gesicht", label: t("registration.bodyFace"), icon: "👤" },
    { value: "Arm", label: t("registration.bodyArm"), icon: "💪" },
    { value: "Hand", label: t("registration.bodyHand"), icon: "✋" },
    { value: "Torso", label: t("registration.bodyTorso"), icon: "🫁" },
    { value: "Bein", label: t("registration.bodyLeg"), icon: "🦵" },
    { value: "Fuss", label: t("registration.bodyFoot"), icon: "🦶" },
  ];

  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAreaToggle = (area) => {
    setFormData((prevData) => {
      const areas = prevData.areas.includes(area)
        ? prevData.areas.filter((a) => a !== area)
        : [...prevData.areas, area];
      return { ...prevData, areas };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setRegistrationStatus("sending");

    try {
      const response = await fetch(`${getBaseUrl()}/api/user/register-mail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, preferredLanguage: i18n.language?.substring(0, 2) || 'de' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error || errorData.message || "Registration failed";
        setServerError(message);
        throw new Error(message);
      }

      setRegistrationStatus("success");
    } catch (error) {
      console.error("Error:", error);
      setRegistrationStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full h-14 pl-12 pr-6 bg-slate-50/80 dark:bg-slate-700/50 border-2 border-transparent focus:border-[#0f7699] dark:focus:border-[#4fc3f7] focus:bg-white dark:focus:bg-slate-700 rounded-2xl text-slate-700 dark:text-slate-200 font-bold placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all outline-none";

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-slate-800/95 backdrop-blur-[20px] rounded-[2.5rem] shadow-[0_24px_80px_rgba(0,0,0,0.25)] border border-white/30 dark:border-slate-700/30 animate-in zoom-in-95 duration-300 custom-scrollbar">
        <div className="p-6 sm:p-12">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="sticky top-0 float-right w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 transition-all hover:bg-red-500 hover:text-white hover:rotate-90 z-10"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-black bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] bg-clip-text text-transparent tracking-tight">
              {t("registration.title")}
            </h2>
          </div>

          {/* Registration Content */}
          <div>
            {registrationStatus === "success" ? (
              <div className="animate-in zoom-in duration-500 text-center py-8">
                <div className="w-20 h-20 bg-green-500 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-green-500/20">
                  <CheckCircle2 size={40} className="text-white" />
                </div>

                <h3 className="text-2xl font-black mb-4 text-green-600 dark:text-green-400 tracking-tight">
                  {t("registration.successTitle")}
                </h3>

                <p className="text-slate-500 dark:text-slate-400 mb-8 font-medium italic">
                  {t("registration.successMessage")}
                </p>

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-700/30 text-amber-800 dark:text-amber-300 p-6 rounded-2xl text-left mb-8 shadow-inner">
                  <p className="text-sm font-medium leading-relaxed">
                    <span className="font-black border-b-2 border-amber-200 dark:border-amber-600 uppercase tracking-widest mr-2">{t("registration.important")}:</span>
                    {t("registration.importantNote")}
                  </p>
                </div>

                <button
                  onClick={onClose}
                  className="bg-slate-900 dark:bg-slate-600 text-white font-black px-10 py-4 rounded-xl transition-all hover:bg-slate-800 dark:hover:bg-slate-500 hover:scale-105 active:scale-95 shadow-lg shadow-slate-900/20 dark:shadow-slate-600/20"
                >
                  {t("registration.close")}
                </button>
              </div>
            ) : registrationStatus === "error" ? (
              <div className="animate-in zoom-in duration-500 text-center py-8">
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
                  <AlertCircle size={40} />
                </div>

                <h3 className="text-2xl font-black mb-4 text-red-600 dark:text-red-400 tracking-tight">
                  {t("registration.errorTitle")}
                </h3>

                <p className="text-slate-500 dark:text-slate-400 mb-8 font-medium">
                  {serverError || t("registration.errorMessage")}
                </p>

                <button
                  onClick={onClose}
                  className="bg-red-600 dark:bg-red-700 text-white font-black px-10 py-4 rounded-xl transition-all hover:bg-red-700 dark:hover:bg-red-600 hover:scale-105 active:scale-95 shadow-xl shadow-red-600/30"
                >
                  {t("registration.close")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="animate-in fade-in duration-500">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {/* First Name */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />
                    </div>
                    <input
                      name="firstName"
                      type="text"
                      placeholder={t("registration.firstName")}
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>

                  {/* Last Name */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />
                    </div>
                    <input
                      name="lastName"
                      type="text"
                      placeholder={t("registration.lastName")}
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>

                  {/* Company */}
                  <div className="sm:col-span-2 relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Building2 size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />
                    </div>
                    <input
                      name="company"
                      type="text"
                      placeholder={t("registration.company")}
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>

                  {/* Email */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />
                    </div>
                    <input
                      name="email"
                      type="email"
                      placeholder={t("registration.email")}
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>

                  {/* Phone */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Phone size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />
                    </div>
                    <input
                      name="phone"
                      type="tel"
                      placeholder={t("registration.phone")}
                      value={formData.phone}
                      onChange={handleChange}
                      pattern="[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*"
                      inputMode="tel"
                      title={t("registration.phoneExample")}
                      required
                      className={inputClasses}
                    />
                  </div>

                  {/* Body Areas */}
                  <div className="sm:col-span-2 mt-2">
                    <h4 className="text-xs font-black text-slate-400 dark:text-slate-500 mb-4 uppercase tracking-widest">
                      {t("registration.bodyAreas")}
                    </h4>

                    <div className="flex flex-wrap gap-2">
                      {bodyAreas.map((area) => (
                        <button
                          key={area.value}
                          type="button"
                          onClick={() => handleAreaToggle(area.value)}
                          className={`
                            flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-bold transition-all border-2
                            ${formData.areas.includes(area.value)
                              ? "bg-[#0f7699] dark:bg-[#0f7699] border-[#0f7699] text-white shadow-lg shadow-[#0f7699]/20"
                              : "bg-slate-50 dark:bg-slate-700 border-transparent text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-600 hover:border-[#0f7699]/20 dark:hover:border-[#4fc3f7]/20"}
                          `}
                        >
                          <span className="text-lg">{area.icon}</span>
                          <span>{area.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Consent Checkbox */}
                  <div className="sm:col-span-2 py-2">
                    <label className="flex items-start gap-4 cursor-pointer group">
                      <div className="relative flex items-center mt-1">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleChange}
                          required
                          className="peer appearance-none w-6 h-6 border-2 border-slate-200 dark:border-slate-600 rounded-lg checked:bg-[#0f7699] checked:border-[#0f7699] transition-all cursor-pointer"
                        />
                        <CheckCircle2 size={16} className="absolute left-1 text-white scale-0 peer-checked:scale-100 transition-transform duration-200 pointer-events-none" />
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed group-hover:text-slate-800 dark:group-hover:text-slate-200 transition-colors">
                        {t("registration.privacyConsent")}{" "}
                        <a
                          href="https://3d-medico.de/impressum.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0f7699] dark:text-[#4fc3f7] font-black hover:underline"
                        >
                          {t("registration.privacyPolicy")}
                        </a>*
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <div className="sm:col-span-2 mt-4 sm:mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting || formData.areas.length === 0 || !formData.consent}
                      className="w-full h-14 rounded-2xl text-base sm:text-[1.1rem] font-black bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] text-white shadow-[0_12px_32px_rgba(15,118,153,0.3)] transition-all duration-300 hover:from-[#0a5f7a] hover:to-[#0f7699] hover:shadow-[0_16px_40px_rgba(15,118,153,0.4)] hover:-translate-y-1 active:translate-y-0 disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none flex items-center justify-center gap-3 overflow-hidden group"
                    >
                      {isSubmitting ? (
                        <Loader2 size={24} className="animate-spin" />
                      ) : (
                        <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      )}
                      <span>
                        {isSubmitting
                          ? t("registration.submitting")
                          : t("registration.submitButton")}
                      </span>
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegistrationForm;
